import {
  Col,
  Row,
  Space,
  Table,
  Card,
  Spin,
  message,
  Divider,
  Button,
  Typography,
  Radio,
  Dropdown,
  Checkbox,
  Input,
} from "antd";
import React, { useCallback, useMemo, useState } from "react";
import {
  PrinterOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  DownOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import "jspdf-autotable";
import {
  HEADERS_EXPORT_DETAIL,
  POST_REPORT_AROTHR_DAILY,
  POST_REPORT_PAYMENT_DAILY,
  POST_REPORT_SUMMARY_PAYMENT,
} from "../../API/apiUrls";
import CurrencyFormat from "../../../hook/CurrencyFormat";
import "../../../assets/font/THSarabunNew-normal";
import "../../../assets/font/THSarabunNew-bold";
import MotionHoc from "../../../utils/MotionHoc";
import TokenCheck from "../../../hook/TokenCheck";
import XLSX from "xlsx-js-style";
import ThaiDatePicker from "../../../hook/ThaiDatePicker";

const { Text } = Typography;

// ✅ helper ระดับ module — ใช้ได้ทั้ง summary / PDF / Excel
const sumRows = (rows) =>
  rows.reduce(
    (acc, r) => {
      const netpay = parseFloat(r.NETPAY) || 0;
      return {
        payamt: acc.payamt + (parseFloat(r.PAYAMT) || 0),
        payamtN: acc.payamtN + (parseFloat(r.PAYAMT_N) || 0),
        disct: acc.disct + (parseFloat(r.DISCT) || 0),
        payamtV: acc.payamtV + (parseFloat(r.PAYAMT_V) || 0),
        cashPay: acc.cashPay + (r.PAYTYP === "01" ? netpay : 0),
        transferPay: acc.transferPay + (r.PAYTYP !== "01" ? netpay : 0),
        netpay: acc.netpay + netpay,
      };
    },
    {
      payamt: 0,
      payamtN: 0,
      disct: 0,
      payamtV: 0,
      cashPay: 0,
      transferPay: 0,
      netpay: 0,
    },
  );

// ✅ รับรวม: แยกประเภทแถวด้วย PAY_TYPE — "NR" = ค่างวด, "AR" = รับอื่นๆ
const isInstallmentRow = (r) => r.PAY_TYPE === "NR";
const isOtherReceiveRow = (r) => r.PAY_TYPE === "AR";

const sumSummaryRows = (rows) =>
  rows.reduce(
    (acc, r) => {
      const payamt = parseFloat(r.PAYAMT) || 0;
      return {
        installment: acc.installment + (isInstallmentRow(r) ? payamt : 0),
        otherReceive: acc.otherReceive + (isOtherReceiveRow(r) ? payamt : 0),
        disct: acc.disct + (parseFloat(r.DISCT) || 0),
        interestReduce: acc.interestReduce + (parseFloat(r.PAYAMT_V) || 0),
        netpay: acc.netpay + (parseFloat(r.NETPAY) || 0),
      };
    },
    { installment: 0, otherReceive: 0, disct: 0, interestReduce: 0, netpay: 0 },
  );

const Main = () => {
  const [
    currencyFormat,
    currencyFormatComma,
    currencyFormatPoint,
    currencyFormatNoPoint,
  ] = CurrencyFormat();
  const roleId = localStorage.getItem("ROLE_ID");
  const showInterestInstallment = roleId === "1" || roleId === "6"; // ค่างวดรวมดอก
  const showAccountInstallment = roleId === "1" || roleId === "12"; // ค่างวดบัญชี

  const [loading, setLoading] = useState(false);
  const [arrayPay, setArrayPay] = useState([]);
  const [company, setCompany] = useState(null);
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs());
  const [docType, setDocType] = useState("normal");
  const [signOut] = TokenCheck();

  const [isFlagCashPay, setIsFlagCashPay] = useState(true);
  const [isFlagTransferPay, setIsFlagTransferPay] = useState(true);
  const [searchFilter, setSearchFilter] = useState("");

  // ──────────────────────────────
  const onQuery = () => {
    if (!startDate || !endDate) {
      message.warning("กรุณาเลือกวันที่ก่อนค้นหา");
      return;
    }
    queryData(
      {
        firstDate: dayjs(startDate).format("YYYY-MM-DDTHH:mm:ssZ"),
        lastDate: dayjs(endDate).format("YYYY-MM-DDTHH:mm:ssZ"),
      },
      docType === "normal"
        ? POST_REPORT_PAYMENT_DAILY
        : docType === "arothr"
          ? POST_REPORT_AROTHR_DAILY
          : POST_REPORT_SUMMARY_PAYMENT,
    );
  };

  const queryData = async (payload, url) => {
    setLoading(true);
    try {
      const res = await axios.post(url, payload, {
        headers: HEADERS_EXPORT_DETAIL,
      });

      if (res.status === 200) {
        const sorted = (res.data.pay ?? [])
          .slice()
          .sort((a, b) => dayjs(a.PAYDT).valueOf() - dayjs(b.PAYDT).valueOf());
        setArrayPay(sorted);
        setCompany(res.data.company?.[0] ?? res.data.Company?.[0] ?? null);
      } else {
        setArrayPay([]);
      }
    } catch (err) {
      console.error(err);
      message.error(
        err?.response?.status > 401
          ? "ไม่พบข้อมูล"
          : "เกิดข้อผิดพลาดในการดึงข้อมูล",
      );
      setArrayPay([]);
      if (err?.response?.status === 401) {
        signOut();
      }
    } finally {
      setLoading(false);
    }
  };

  const fmt = (val) => (val ? currencyFormatPoint(val) : "");
  const fmtPos = (val) => (parseFloat(val) > 0 ? currencyFormatPoint(val) : "");

  const handleFilterData = useCallback(() => {
    if (!arrayPay.length) return [];

    const search = searchFilter.trim().toLowerCase();

    return arrayPay.filter((item) => {
      if (isFlagTransferPay && isFlagCashPay) {
        // ok
      } else if (isFlagTransferPay && !isFlagCashPay) {
        if (item.PAYTYP === "01") return false;
      } else if (!isFlagTransferPay && isFlagCashPay) {
        if (item.PAYTYP !== "01") return false;
      } else {
        // ✅ ทั้งคู่ false → ไม่มีข้อมูล
        return false;
      }

      if (search) {
        const contno = String(
          docType === "normal" ? (item.CONTNO ?? "") : (item.newContno ?? ""),
        ).toLowerCase();
        const bill = String(item.TMBILL ?? "").toLowerCase();
        if (!contno.includes(search) && !bill.includes(search)) return false;
      }

      return true;
    });
  }, [arrayPay, isFlagTransferPay, isFlagCashPay, searchFilter, docType]);

  // ──────────────────────────────
  // Columns
  // ──────────────────────────────
  const columns = [
    {
      title: "สาขา",
      dataIndex: "LOCAT",
      key: "LOCAT",
      align: "center",
      width: 60,
    },
    {
      title: "เลขที่ใบรับ",
      dataIndex: "TMBILL",
      key: "TMBILL",
      align: "center",
      width: 120,
    },
    {
      title: "วันที่รับ",
      dataIndex: "PAYDT",
      key: "PAYDT",
      align: "center",
      width: 100,
      sorter: (a, b) => dayjs(a.PAYDT).valueOf() - dayjs(b.PAYDT).valueOf(),
      render: (val) => (val ? dayjs(val).format("DD/MM/YYYY") : "-"),
    },
    {
      title: "เลขที่สัญญา/ชื่อ",
      align: "center",
      width: 130,
      render: (_, record) =>
        docType === "normal" ? record.CONTNO : record.newContno,
    },
    {
      title: "ประเภท",
      dataIndex: "PAYTYP",
      key: "PAYTYP",
      align: "center",
      width: 65,
    },
    showInterestInstallment && {
      title: "ค่างวดรวมดอก",
      key: "PAYAMT",
      align: "right",
      width: 90,
      sorter: (a, b) => (a.PAYAMT ?? 0) - (b.PAYAMT ?? 0),
      render: (_, record) =>
        docType === "normal" ? (record.PAYAMT ? fmt(record.PAYAMT) : 0) : null,
    },
    showAccountInstallment && {
      title: "ค่างวดบัญชี",
      key: "PAYAMT_N",
      align: "right",
      width: 90,
      sorter: (a, b) => (a.PAYAMT_N ?? 0) - (b.PAYAMT_N ?? 0),
      render: (_, record) =>
        docType === "normal"
          ? record.PAYAMT_N
            ? fmt(record.PAYAMT_N)
            : 0
          : null,
    },
    {
      title: "ดาวน์",
      align: "right",
      width: 80,
      render: () => null,
    },
    {
      title: "ปรับ",
      align: "right",
      width: 80,
      render: () => null,
    },
    {
      title: "ขายสด",
      align: "right",
      width: 80,
      render: () => null,
    },
    {
      title: "เงินจอง",
      align: "right",
      width: 80,
      render: (_, r) =>
        r.FLAG === "C" ? (
          <Text style={{ color: "#e74c3c", fontWeight: "bold" }}>
            **ยกเลิก**
          </Text>
        ) : null,
    },
    {
      title: "รับอื่นๆ",
      align: "right",
      width: 80,
      sorter: (a, b) => (a.PAYAMT ?? 0) - (b.PAYAMT ?? 0),
      render: (_, record) =>
        docType === "arothr" ? (record.PAYAMT ? fmt(record.PAYAMT) : 0) : null,
    },
    {
      title: "ส่วนลด",
      key: "DISCT",
      align: "right",
      width: 80,
      sorter: (a, b) => (a.DISCT ?? 0) - (b.DISCT ?? 0),
      render: (_, record) => fmt(record.DISCT || 0),
    },
    {
      title: "ดอกเบี้ย",
      key: "PAYAMT_V",
      align: "right",
      width: 90,
      sorter: (a, b) => (a.PAYAMT_V ?? 0) - (b.PAYAMT_V ?? 0),
      // ✅ แก้ signature จาก (record) → (_, record)
      render: (_, record) => (record.PAYAMT_V ? fmt(record.PAYAMT_V) : null),
    },
    {
      title: "เงินสด",
      key: "cashPay",
      align: "right",
      width: 90,
      sorter: (a, b) => {
        const valA = a.NETPAY && a.PAYTYP === "01" ? a.NETPAY : 0;
        const valB = b.NETPAY && b.PAYTYP === "01" ? b.NETPAY : 0;
        return valA - valB;
      },
      render: (_, record) =>
        record.NETPAY && record.PAYTYP === "01" ? fmt(record.NETPAY) : 0,
    },
    {
      title: "เงินโอน",
      key: "transferPay",
      align: "right",
      width: 90,
      sorter: (a, b) => {
        const valA = a.NETPAY && a.PAYTYP !== "01" ? a.NETPAY : 0;
        const valB = b.NETPAY && b.PAYTYP !== "01" ? b.NETPAY : 0;
        return valA - valB;
      },
      render: (_, record) =>
        record.NETPAY && record.PAYTYP !== "01" ? fmt(record.NETPAY) : 0,
    },
    {
      title: "เงินรับฝากค่างวด",
      align: "right",
      width: 120,
      render: () => null,
    },
    showAccountInstallment &&
      docType === "normal" && {
        title: "รับรวมสุทธิ",
        align: "right",
        width: 120,
        render: (_, record) =>
          docType === "normal"
            ? record.NETPAY
              ? fmt(record.NETPAY)
              : 0
            : null,
      },
  ].filter(Boolean);

  // ──────────────────────────────
  // Columns — รับรวม (summary)
  // ──────────────────────────────
  const summaryColumns = [
    {
      title: "สาขา",
      dataIndex: "LOCAT",
      key: "LOCAT",
      align: "center",
      width: 60,
    },
    {
      title: "เลขที่ใบรับ",
      dataIndex: "TMBILL",
      key: "TMBILL",
      align: "center",
      width: 120,
    },
    {
      title: "วันที่รับ",
      dataIndex: "PAYDT",
      key: "PAYDT",
      align: "center",
      width: 100,
      sorter: (a, b) => dayjs(a.PAYDT).valueOf() - dayjs(b.PAYDT).valueOf(),
      render: (val) => (val ? dayjs(val).format("DD/MM/YYYY") : "-"),
    },
    {
      title: "เลขที่สัญญา/ชื่อ",
      align: "center",
      width: 130,
      render: (_, record) => record.newContno || "",
    },
    {
      title: "ค่างวด",
      key: "installment",
      align: "right",
      width: 90,
      sorter: (a, b) =>
        (isInstallmentRow(a) ? (a.PAYAMT ?? 0) : 0) -
        (isInstallmentRow(b) ? (b.PAYAMT ?? 0) : 0),
      render: (_, record) =>
        isInstallmentRow(record) && record.PAYAMT ? fmt(record.PAYAMT) : null,
    },
    {
      title: "ดาวน์",
      align: "right",
      width: 80,
      render: () => null,
    },
    {
      title: "ปรับ",
      align: "right",
      width: 80,
      render: () => null,
    },
    {
      title: "ขายสด",
      align: "right",
      width: 80,
      render: () => null,
    },
    {
      title: "เงินจอง",
      align: "right",
      width: 80,
      render: (_, r) =>
        r.FLAG === "C" ? (
          <Text style={{ color: "#e74c3c", fontWeight: "bold" }}>
            **ยกเลิก**
          </Text>
        ) : null,
    },
    {
      title: "รับอื่นๆ",
      key: "otherReceive",
      align: "right",
      width: 80,
      sorter: (a, b) =>
        (isOtherReceiveRow(a) ? (a.PAYAMT ?? 0) : 0) -
        (isOtherReceiveRow(b) ? (b.PAYAMT ?? 0) : 0),
      render: (_, record) =>
        isOtherReceiveRow(record) && record.PAYAMT ? fmt(record.PAYAMT) : null,
    },
    {
      title: "ส่วนลด",
      key: "DISCT",
      align: "right",
      width: 80,
      sorter: (a, b) => (a.DISCT ?? 0) - (b.DISCT ?? 0),
      render: (_, record) => fmt(record.DISCT || 0),
    },
    {
      title: "ลดดอกเบี้ย",
      key: "PAYAMT_V",
      align: "right",
      width: 90,
      sorter: (a, b) => (a.PAYAMT_V ?? 0) - (b.PAYAMT_V ?? 0),
      render: (_, record) => (record.PAYAMT_V ? fmt(record.PAYAMT_V) : null),
    },
    {
      title: "เงินสด",
      key: "NETPAY",
      align: "right",
      width: 90,
      sorter: (a, b) => (a.NETPAY ?? 0) - (b.NETPAY ?? 0),
      render: (_, record) => (record.NETPAY ? fmt(record.NETPAY) : 0),
    },
    {
      title: "เงินรับฝากค่างวด",
      align: "right",
      width: 120,
      render: () => null,
    },
  ];

  const activeColumns = docType === "summary" ? summaryColumns : columns;

  const exportMenuItems = {
    items: [
      {
        key: "pdf",
        label: "Export PDF",
        icon: <FilePdfOutlined style={{ color: "#c0392b" }} />,
      },
      {
        key: "excel",
        label: "Export Excel",
        icon: <FileExcelOutlined style={{ color: "#27ae60" }} />,
      },
    ],
    onClick: ({ key }) => {
      if (key === "pdf") handlePrintPDF();
      if (key === "excel") handleExportExcel();
    },
  };

  const rowClassName = (record) => (record.FLAG === "C" ? "row-cancelled" : "");
  console.log("arrayPay", arrayPay);

  // ──────────────────────────────
  // PDF
  // ──────────────────────────────
  const handlePrintPDF = () => {
    if (!filteredData.length) {
      message.warning("ไม่มีข้อมูลสำหรับพิมพ์");
      return;
    }

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    const now = dayjs().format("DD/MM/YYYY HH:mm:ss");
    const fromDate = startDate ? dayjs(startDate).format("DD/MM/YYYY") : "";
    const toDate = endDate ? dayjs(endDate).format("DD/MM/YYYY") : "";

    const drawHeader = (pageNum) => {
      if (pageNum === 1) {
        doc.setFont("THSarabunNew", "bold");
        doc.setFontSize(16);
        doc.text(
          `รายงานการรับชำระประจำวัน (${docType === "normal" ? "รับปกติ" : docType === "arothr" ? "ลูกหนี้อื่น ๆ" : "รับรวม"})`,
          pageWidth / 2,
          10,
          { align: "center" },
        );
        doc.setFont("THSarabunNew", "normal");
        doc.setFontSize(14);
        doc.text(company?.COMP_NM ?? "", pageWidth / 2, 17, {
          align: "center",
        });
        doc.text(company?.COMP_ADR1 ?? "", pageWidth / 2, 23, {
          align: "center",
        });
        doc.text(
          `จากวันที่  ${fromDate}   ถึงวันที่  ${toDate}`,
          pageWidth / 2,
          29,
          { align: "center" },
        );
        doc.setFontSize(12);
        doc.text(`วันที่พิมพ์ ${now}`, 8, 35);
        doc.text(`หน้าที่  ${pageNum}`, pageWidth - 8, 35, { align: "right" });
      } else {
        doc.setFont("THSarabunNew", "normal");
        doc.setFontSize(12);
        doc.text(`วันที่พิมพ์ ${now}`, 8, 8);
        doc.text(`หน้าที่  ${pageNum}`, pageWidth - 8, 8, { align: "right" });
      }
    };

    const cancelledRows = filteredData.filter((r) => r.FLAG === "C");
    const validRows = filteredData.filter((r) => r.FLAG !== "C");

    const isSummary = docType === "summary";

    const head = [
      isSummary
        ? [
            "สาขา",
            "เลขที่ใบรับ",
            "วันที่รับ",
            "เลขที่สัญญา/ชื่อ",
            "ค่างวด",
            "ดาวน์",
            "ปรับ",
            "ขายสด",
            "เงินจอง",
            "รับอื่นๆ",
            "ส่วนลด",
            "ลดดอกเบี้ย",
            "เงินสด",
            "เงินรับฝากค่างวด",
          ]
        : [
            "สาขา",
            "เลขที่ใบรับ",
            "วันที่รับ",
            "เลขที่สัญญา/ชื่อ",
            "ประเภท",
            ...(showInterestInstallment ? ["ค่างวดรวมดอก"] : []),
            ...(showAccountInstallment ? ["ค่างวดบัญชี"] : []),
            "ดาวน์",
            "ปรับ",
            "ขายสด",
            "เงินจอง",
            "รับอื่นๆ",
            "ส่วนลด",
            "ดอกเบี้ย",
            "เงินสด",
            "เงินโอน",
            "เงินรับฝากค่างวด",
            ...(showAccountInstallment && docType === "normal"
              ? ["รับรวมสุทธิ"]
              : []), // ✅
          ],
    ];

    const tableBody = filteredData.map((r) => {
      const cancelled = r.FLAG === "C";

      if (isSummary) {
        return [
          r.LOCAT ?? "",
          r.TMBILL ?? "",
          r.PAYDT ? dayjs(r.PAYDT).format("DD/MM/YYYY") : "",
          r.newContno ?? "",
          isInstallmentRow(r) && r.PAYAMT ? fmt(r.PAYAMT) : "", // ค่างวด
          "", // ดาวน์
          "", // ปรับ
          "", // ขายสด
          cancelled ? "**ยกเลิก**" : "", // เงินจอง
          isOtherReceiveRow(r) && r.PAYAMT ? fmt(r.PAYAMT) : "", // รับอื่นๆ
          r.DISCT > 0 ? fmt(r.DISCT) : "", // ส่วนลด
          r.PAYAMT_V > 0 ? fmt(r.PAYAMT_V) : "", // ลดดอกเบี้ย
          r.NETPAY ? fmt(r.NETPAY) : "", // เงินสด
          "", // เงินรับฝากค่างวด
        ];
      }

      return [
        r.LOCAT ?? "",
        r.TMBILL ?? "",
        r.PAYDT ? dayjs(r.PAYDT).format("DD/MM/YYYY") : "",
        docType === "normal" ? (r.CONTNO ?? "") : (r.newContno ?? ""),
        r.PAYTYP ?? "",
        ...(showInterestInstallment
          ? [docType === "normal" ? (r.PAYAMT ? fmt(r.PAYAMT) : "") : ""]
          : []),
        ...(showAccountInstallment
          ? [docType === "normal" ? (r.PAYAMT_N ? fmt(r.PAYAMT_N) : "") : ""]
          : []),
        "", // ดาวน์
        "", // ปรับ
        "", // ขายสด
        cancelled ? "**ยกเลิก**" : "", // เงินจอง
        docType === "arothr" ? (r.PAYAMT ? fmt(r.PAYAMT) : "") : "", // รับอื่นๆ
        r.DISCT > 0 ? fmt(r.DISCT) : "", // ส่วนลด
        r.PAYAMT_V > 0 ? fmt(r.PAYAMT_V) : "", // ดอกเบี้ย
        r.NETPAY && r.PAYTYP === "01" ? fmt(r.NETPAY) : "", // เงินสด
        r.NETPAY && r.PAYTYP !== "01" ? fmt(r.NETPAY) : "", // เงินโอน
        "", // เงินรับฝากค่างวด
        ...(showAccountInstallment && docType === "normal"
          ? [r.NETPAY ? fmt(r.NETPAY) : ""]
          : []), // ✅ รับรวมสุทธิ
      ];
    });

    const makeSummaryRowSummary = (
      label,
      count,
      t,
      textColor = [0, 0, 0],
      fillColor = [236, 240, 241],
    ) => {
      const cell = (content) => ({
        content,
        styles: {
          halign: "right",
          fontStyle: "bold",
          font: "THSarabunNew",
          fillColor,
          textColor,
        },
      });
      return [
        {
          content: `${label}  ${count} รายการ  จำนวนเงิน`,
          colSpan: 4,
          styles: {
            halign: "right",
            fontStyle: "bold",
            font: "THSarabunNew",
            fillColor,
            textColor,
          },
        },
        cell(t.installment > 0 ? currencyFormatPoint(t.installment) : ""), // ค่างวด
        cell(""), // ดาวน์
        cell(""), // ปรับ
        cell(""), // ขายสด
        cell(""), // เงินจอง
        cell(t.otherReceive > 0 ? currencyFormatPoint(t.otherReceive) : ""), // รับอื่นๆ
        cell(t.disct > 0 ? currencyFormatPoint(t.disct) : ""), // ส่วนลด
        cell(t.interestReduce > 0 ? currencyFormatPoint(t.interestReduce) : ""), // ลดดอกเบี้ย
        cell(t.netpay > 0 ? currencyFormatPoint(t.netpay) : ""), // เงินสด
        cell(""), // เงินรับฝากค่างวด
      ];
    };

    const makeSummaryRow = (
      label,
      count,
      t,
      textColor = [0, 0, 0],
      fillColor = [236, 240, 241],
    ) => {
      const cell = (content) => ({
        content,
        styles: {
          halign: "right",
          fontStyle: "bold",
          font: "THSarabunNew",
          fillColor,
          textColor,
        },
      });
      return [
        {
          content: `${label}  ${count} รายการ  จำนวนเงิน`,
          colSpan: 5,
          styles: {
            halign: "right",
            fontStyle: "bold",
            font: "THSarabunNew",
            fillColor,
            textColor,
          },
        },
        ...(showInterestInstallment
          ? [
              cell(
                docType === "normal" && t.payamt > 0
                  ? currencyFormatPoint(t.payamt)
                  : "",
              ),
            ]
          : []),
        ...(showAccountInstallment
          ? [
              cell(
                docType === "normal" && t.payamtN > 0
                  ? currencyFormatPoint(t.payamtN)
                  : "",
              ),
            ]
          : []),
        cell(""), // ดาวน์
        cell(""), // ปรับ
        cell(""), // ขายสด
        cell(""), // เงินจอง
        cell(
          docType === "arothr" && t.payamt > 0
            ? currencyFormatPoint(t.payamt)
            : "",
        ), // รับอื่นๆ
        cell(t.disct > 0 ? currencyFormatPoint(t.disct) : ""), // ส่วนลด
        cell(t.payamtV > 0 ? currencyFormatPoint(t.payamtV) : ""), // ดอกเบี้ย
        cell(t.cashPay > 0 ? currencyFormatPoint(t.cashPay) : ""), // เงินสด
        cell(t.transferPay > 0 ? currencyFormatPoint(t.transferPay) : ""), // เงินโอน
        cell(""), // เงินรับฝากค่างวด
        ...(showAccountInstallment && docType === "normal"
          ? [cell(t.netpay > 0 ? currencyFormatPoint(t.netpay) : "")]
          : []), // ✅ รับรวมสุทธิ
      ];
    };

    const totalsAll = isSummary
      ? sumSummaryRows(filteredData)
      : sumRows(filteredData);
    const totalsCancelled = isSummary
      ? sumSummaryRows(cancelledRows)
      : sumRows(cancelledRows);
    const totalsValid = isSummary
      ? sumSummaryRows(validRows)
      : sumRows(validRows);

    const buildRow = isSummary ? makeSummaryRowSummary : makeSummaryRow;

    const summaryRows = [
      buildRow("รวมทั้งหมด", filteredData.length, totalsAll),
      buildRow(
        "รวมยกเลิกทั้งหมด",
        cancelledRows.length,
        totalsCancelled,
        [220, 53, 69],
        [255, 235, 235],
      ),
      buildRow(
        "รวมรับชำระจริง",
        validRows.length,
        totalsValid,
        [21, 87, 36],
        [212, 237, 218],
      ),
    ];

    doc.autoTable({
      startY: 38,
      head,
      body: [...tableBody, ...summaryRows],
      styles: {
        font: "THSarabunNew",
        fontSize: 12,
        cellPadding: 1.2,
        halign: "center",
      },
      headStyles: {
        font: "THSarabunNew",
        fillColor: [52, 73, 94],
        textColor: 255,
        halign: "center",
        fontStyle: "bold",
        fontSize: 14,
      },
      alternateRowStyles: { fillColor: [250, 250, 250] },
      didParseCell: (data) => {
        if (data.section === "body" && data.row.index < tableBody.length) {
          const row = filteredData[data.row.index];
          if (row?.FLAG === "C") {
            data.cell.styles.textColor = [220, 53, 69];
            data.cell.styles.fontStyle = "bold";
          }
        }
      },
      didDrawPage: () =>
        drawHeader(doc.internal.getCurrentPageInfo().pageNumber),
      tableWidth: "auto",
      margin: { top: 41, left: 3, right: 3 },
    });

    doc.save(`รายงานรับชำระประจำวัน_${fromDate}_${toDate}.pdf`);
  };

  // ──────────────────────────────
  // Excel
  // ──────────────────────────────
  const handleExportExcel = () => {
    if (!filteredData.length) {
      message.warning("ไม่มีข้อมูล");
      return;
    }

    const cancelledRows = filteredData.filter((r) => r.FLAG === "C");
    const validRows = filteredData.filter((r) => r.FLAG !== "C");
    const isSummary = docType === "summary";
    const totalsAll = isSummary
      ? sumSummaryRows(filteredData)
      : sumRows(filteredData);
    const totalsCancelled = isSummary
      ? sumSummaryRows(cancelledRows)
      : sumRows(cancelledRows);
    const totalsValid = isSummary
      ? sumSummaryRows(validRows)
      : sumRows(validRows);

    const rows = filteredData.map((r, index) => {
      if (isSummary) {
        return {
          ลำดับ: index + 1,
          สาขา: r.LOCAT ?? "",
          เลขที่ใบรับ: r.TMBILL ?? "",
          วันที่รับ: r.PAYDT ? dayjs(r.PAYDT).format("DD/MM/YYYY") : "",
          "เลขที่สัญญา/ชื่อ": r.newContno ?? "",
          ค่างวด: isInstallmentRow(r) ? parseFloat(r.PAYAMT) || 0 : 0,
          ดาวน์: 0,
          ปรับ: 0,
          ขายสด: 0,
          เงินจอง: r.FLAG === "C" ? "ยกเลิก" : 0,
          รับอื่นๆ: isOtherReceiveRow(r) ? parseFloat(r.PAYAMT) || 0 : 0,
          ส่วนลด: parseFloat(r.DISCT) || 0,
          ลดดอกเบี้ย: parseFloat(r.PAYAMT_V) || 0,
          เงินสด: parseFloat(r.NETPAY) || 0,
          เงินรับฝากค่างวด: 0,
        };
      }

      const base = {
        ลำดับ: index + 1,
        สาขา: r.LOCAT ?? "",
        เลขที่ใบรับ: r.TMBILL ?? "",
        วันที่รับ: r.PAYDT ? dayjs(r.PAYDT).format("DD/MM/YYYY") : "",
        "เลขที่สัญญา/ชื่อ":
          docType === "normal" ? (r.CONTNO ?? "") : (r.newContno ?? ""),
        ประเภท: r.PAYTYP ?? "",
      };

      if (showInterestInstallment) {
        base.ค่างวดรวมดอก =
          docType === "normal" ? parseFloat(r.PAYAMT) || 0 : 0;
      }
      if (showAccountInstallment) {
        base.ค่างวดบัญชี =
          docType === "normal" ? parseFloat(r.PAYAMT_N) || 0 : 0;
      }

      return {
        ...base,
        ดาวน์: 0,
        ปรับ: 0,
        ขายสด: 0,
        เงินจอง: r.FLAG === "C" ? "ยกเลิก" : 0,
        รับอื่นๆ: docType === "arothr" ? parseFloat(r.PAYAMT) || 0 : 0,
        ส่วนลด: parseFloat(r.DISCT) || 0,
        ดอกเบี้ย: parseFloat(r.PAYAMT_V) || 0,
        เงินสด: r.PAYTYP === "01" ? parseFloat(r.NETPAY) || 0 : 0,
        เงินโอน: r.PAYTYP !== "01" ? parseFloat(r.NETPAY) || 0 : 0,
        เงินรับฝากค่างวด: 0,
        ...(showAccountInstallment && docType === "normal"
          ? { รับรวมสุทธิ: parseFloat(r.NETPAY) || 0 }
          : {}), // ✅
      };
    });

    const keys = Object.keys(rows[0]);
    const moneyKeys = isSummary
      ? [
          "ค่างวด",
          "ดาวน์",
          "ปรับ",
          "ขายสด",
          "รับอื่นๆ",
          "ส่วนลด",
          "ลดดอกเบี้ย",
          "เงินสด",
          "เงินรับฝากค่างวด",
        ]
      : [
          ...(showInterestInstallment ? ["ค่างวดรวมดอก"] : []),
          ...(showAccountInstallment ? ["ค่างวดบัญชี"] : []),
          "ดาวน์",
          "ปรับ",
          "ขายสด",
          "รับอื่นๆ",
          "ส่วนลด",
          "ดอกเบี้ย",
          "เงินสด",
          "เงินโอน",
          "เงินรับฝากค่างวด",
          ...(showAccountInstallment && docType === "normal"
            ? ["รับรวมสุทธิ"]
            : []), // ✅
        ];

    const makeSummaryRow = (label, count, t) => {
      const row = Object.fromEntries(keys.map((k) => [k, ""]));
      row["เลขที่ใบรับ"] = `${label}  ${count} รายการ`;

      if (isSummary) {
        row["ค่างวด"] = t.installment || 0;
        row["รับอื่นๆ"] = t.otherReceive || 0;
        row["ส่วนลด"] = t.disct || 0;
        row["ลดดอกเบี้ย"] = t.interestReduce || 0;
        row["เงินสด"] = t.netpay || 0;
        return row;
      }

      if (docType === "normal") {
        if (showInterestInstallment) row["ค่างวดรวมดอก"] = t.payamt || 0;
        if (showAccountInstallment) row["ค่างวดบัญชี"] = t.payamtN || 0;
      } else if (docType === "arothr") {
        row["รับอื่นๆ"] = t.payamt || 0;
      }
      row["ส่วนลด"] = t.disct || 0;
      row["ดอกเบี้ย"] = t.payamtV || 0;
      row["เงินสด"] = t.cashPay || 0;
      row["เงินโอน"] = t.transferPay || 0;
      if (showAccountInstallment && docType === "normal") {
        row["รับรวมสุทธิ"] = t.netpay || 0;
      }
      return row;
    };

    const sumAll = makeSummaryRow("รวมทั้งหมด", filteredData.length, totalsAll);
    const sumCancelled = makeSummaryRow(
      "รวมยกเลิกทั้งหมด",
      cancelledRows.length,
      totalsCancelled,
    );
    const sumValid = makeSummaryRow(
      "รวมรับชำระจริง",
      validRows.length,
      totalsValid,
    );

    // ── styles ────────────────────────────────────────────
    const border = {
      top: { style: "thin", color: { rgb: "CCCCCC" } },
      bottom: { style: "thin", color: { rgb: "CCCCCC" } },
      left: { style: "thin", color: { rgb: "CCCCCC" } },
      right: { style: "thin", color: { rgb: "CCCCCC" } },
    };
    const fills = {
      even: { patternType: "solid", fgColor: { rgb: "EBF5FF" } },
      odd: { patternType: "solid", fgColor: { rgb: "FFFFFF" } },
      cancel: { patternType: "solid", fgColor: { rgb: "FDECEA" } },
      header: { patternType: "solid", fgColor: { rgb: "34495E" } },
      sumAll: { patternType: "solid", fgColor: { rgb: "ECF0F1" } },
      sumCancel: { patternType: "solid", fgColor: { rgb: "FFEBEB" } },
      sumValid: { patternType: "solid", fgColor: { rgb: "D5E8D4" } },
    };

    const ws = XLSX.utils.json_to_sheet([
      ...rows,
      sumAll,
      sumCancelled,
      sumValid,
    ]);

    // header
    keys.forEach((_, ci) => {
      const ref = XLSX.utils.encode_cell({ r: 0, c: ci });
      if (!ws[ref]) return;
      ws[ref].s = {
        fill: fills.header,
        font: { bold: true, sz: 12, color: { rgb: "FFFFFF" } },
        alignment: { horizontal: "center", vertical: "center" },
        border,
      };
    });

    // data rows
    rows.forEach((row, ri) => {
      const isCancel = row["เงินจอง"] === "ยกเลิก";
      keys.forEach((key, ci) => {
        const ref = XLSX.utils.encode_cell({ r: ri + 1, c: ci });
        if (!ws[ref]) ws[ref] = { v: "", t: "s" };
        const isMoney = moneyKeys.includes(key);
        ws[ref].s = {
          fill: isCancel ? fills.cancel : ri % 2 === 0 ? fills.even : fills.odd,
          font: {
            sz: 11,
            color: isCancel ? { rgb: "E74C3C" } : { rgb: "000000" },
            bold: isCancel,
          },
          alignment: {
            horizontal: isMoney ? "right" : "center",
            vertical: "center",
          },
          border,
          numFmt:
            isMoney && typeof row[key] === "number" ? "#,##0.00" : undefined,
        };
      });
    });

    // summary rows
    const summaryDefs = [
      { offset: 1, fill: fills.sumAll, color: "000000" },
      { offset: 2, fill: fills.sumCancel, color: "DC3545" },
      { offset: 3, fill: fills.sumValid, color: "155724" },
    ];
    summaryDefs.forEach(({ offset, fill, color }) => {
      const ri = rows.length + offset;
      keys.forEach((key, ci) => {
        const ref = XLSX.utils.encode_cell({ r: ri, c: ci });
        if (!ws[ref]) return;
        const isMoney = moneyKeys.includes(key);
        ws[ref].s = {
          fill,
          font: { bold: true, sz: 12, color: { rgb: color } },
          alignment: {
            horizontal: isMoney ? "right" : "left",
            vertical: "center",
          },
          border,
          numFmt: isMoney ? "#,##0.00" : undefined,
        };
      });
    });

    // column width
    const colWidthMap = {
      ลำดับ: 6,
      สาขา: 8,
      เลขที่ใบรับ: 22,
      วันที่รับ: 12,
      "เลขที่สัญญา/ชื่อ": 18,
      ประเภท: 10,
      ค่างวดรวมดอก: 14,
      ค่างวดบัญชี: 14,
      ค่างวด: 14,
      ดาวน์: 10,
      ปรับ: 10,
      ขายสด: 10,
      เงินจอง: 12,
      รับอื่นๆ: 12,
      ส่วนลด: 12,
      ดอกเบี้ย: 12,
      ลดดอกเบี้ย: 12,
      เงินสด: 12,
      เงินโอน: 12,
      เงินรับฝากค่างวด: 18,
      รับรวมสุทธิ: 14, // ✅
    };
    ws["!cols"] = keys.map((k) => ({ wch: colWidthMap[k] ?? 14 }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "รายงาน");
    XLSX.writeFile(wb, `รายงานรับชำระ_${dayjs().format("DDMMYYYY")}.xlsx`);
    message.success("Export Excel สำเร็จ");
  };

  const hasDate = startDate && endDate;
  const filteredData = useMemo(() => handleFilterData(), [handleFilterData]);

  return (
    <>
      <style>{`
        .row-cancelled td {
          color: #e74c3c !important;
          font-weight: bold;
        }
      `}</style>

      <Spin spinning={loading} size="large" tip="Loading...">
        <Card
          size="small"
          style={{
            marginBottom: 16,
            background: "#fafafa",
            borderRadius: 10,
          }}
          styles={{ body: { padding: "14px 16px" } }}
        >
          <Row justify="space-between" align="middle" gutter={[12, 12]}>
            <Col flex="auto">
              <Space size={12} wrap>
                <Radio.Group
                  value={docType}
                  onChange={(e) => {
                    setDocType(e.target.value);
                    setArrayPay([]);
                  }}
                  buttonStyle="solid"
                >
                  <Radio.Button value="normal">รับปกติ</Radio.Button>
                  <Radio.Button value="arothr">ลูกหนี้อื่น ๆ</Radio.Button>
                  <Radio.Button value="summary">รับรวม</Radio.Button>
                </Radio.Group>
                เริ่ม
                <ThaiDatePicker
                  size="large"
                  value={startDate}
                  onChange={setStartDate}
                  placeholder={"วันที่เริ่มต้น"}
                  allowClear={false}
                />
                ถึง
                <ThaiDatePicker
                  size="large"
                  value={endDate}
                  onChange={setEndDate}
                  placeholder={"วันที่สิ้นสุด"}
                  allowClear={false}
                />
                <Space
                  size={16}
                  style={{
                    height: 40,
                    padding: "0 14px",
                    background: "#fff",
                    border: "1px solid #d9d9d9",
                    borderRadius: 6,
                  }}
                >
                  <Checkbox
                    checked={isFlagCashPay}
                    onChange={(e) => setIsFlagCashPay(e.target.checked)}
                  >
                    เงินสด
                  </Checkbox>
                  <Checkbox
                    checked={isFlagTransferPay}
                    onChange={(e) => setIsFlagTransferPay(e.target.checked)}
                  >
                    เงินโอน
                  </Checkbox>
                </Space>
                <Button size="large" type="primary" onClick={onQuery}>
                  ค้นหา
                </Button>
                <Input
                  size="small"
                  placeholder="ค้นหาเลขที่สัญญา / เลขที่ใบรับ"
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  allowClear
                  prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                  style={{ width: 260 }}
                />
              </Space>
            </Col>
            <Col>
              <Dropdown menu={exportMenuItems} disabled={!filteredData.length}>
                <Button size="large" icon={<PrinterOutlined />}>
                  พิมพ์ / Export <DownOutlined />
                </Button>
              </Dropdown>
            </Col>
          </Row>
        </Card>

        <Card style={{ marginBottom: "10px" }}>
          <Divider style={{ fontSize: 20 }}>
            รายงานการรับชำระประจำวัน (
            {docType === "normal"
              ? "รับปกติ"
              : docType === "arothr"
                ? "ลูกหนี้อื่น ๆ"
                : "รับรวม"}
            )
          </Divider>

          {company && (
            <div style={{ marginBottom: 4, textAlign: "center" }}>
              <Typography.Text
                strong
                style={{ display: "block", fontSize: 16 }}
              >
                {company.COMP_NM}
              </Typography.Text>
              <Typography.Text
                type="secondary"
                style={{ display: "block", fontSize: 16 }}
              >
                {company.COMP_ADR1}
              </Typography.Text>
            </div>
          )}

          {hasDate && (
            <Typography.Text
              style={{ display: "block", textAlign: "center", fontSize: 16 }}
            >
              จากวันที่ {dayjs(startDate).format("DD/MM/YYYY")} ถึงวันที่{" "}
              {dayjs(endDate).format("DD/MM/YYYY")}
            </Typography.Text>
          )}

          <Table
            style={{ marginTop: "10px" }}
            size="small"
            columns={activeColumns}
            dataSource={filteredData}
            rowKey={(record) =>
              record.wctId ?? `${record.TMBILL}-${record.CONTNO}`
            }
            rowClassName={rowClassName}
            scroll={{ x: 1400 }}
            pagination={{
              defaultPageSize: 50,
              showSizeChanger: true,
              showTotal: (total) => `ทั้งหมด ${total} รายการ`,
              pageSizeOptions: ["10", "20", "50", "100"],
            }}
            summary={() => {
              const cancelledRows = filteredData.filter((r) => r.FLAG === "C");
              const validRows = filteredData.filter((r) => r.FLAG !== "C");

              if (docType === "summary") {
                const totalsAll = sumSummaryRows(filteredData);
                const totalsCancelled = sumSummaryRows(cancelledRows);
                const totalsValid = sumSummaryRows(validRows);

                const SummaryRowSummary = ({
                  label,
                  count,
                  t,
                  style = {},
                  cellStyle = {},
                }) => (
                  <Table.Summary.Row
                    style={{
                      background: "#ecf0f1",
                      fontWeight: "bold",
                      ...style,
                    }}
                  >
                    {/* 1-4: สาขา → เลขที่สัญญา/ชื่อ */}
                    <Table.Summary.Cell
                      colSpan={4}
                      align="right"
                      style={cellStyle}
                    >
                      {label} {count} รายการ จำนวนเงิน :
                    </Table.Summary.Cell>

                    {/* ค่างวด */}
                    <Table.Summary.Cell align="right" style={cellStyle}>
                      {t.installment ? fmtPos(t.installment) : null}
                    </Table.Summary.Cell>

                    {/* ดาวน์, ปรับ, ขายสด, เงินจอง — empty */}
                    <Table.Summary.Cell />
                    <Table.Summary.Cell />
                    <Table.Summary.Cell />
                    <Table.Summary.Cell />

                    {/* รับอื่นๆ */}
                    <Table.Summary.Cell align="right" style={cellStyle}>
                      {t.otherReceive ? fmtPos(t.otherReceive) : null}
                    </Table.Summary.Cell>

                    {/* ส่วนลด */}
                    <Table.Summary.Cell align="right" style={cellStyle}>
                      {t.disct ? fmtPos(t.disct) : null}
                    </Table.Summary.Cell>

                    {/* ลดดอกเบี้ย */}
                    <Table.Summary.Cell align="right" style={cellStyle}>
                      {t.interestReduce ? fmtPos(t.interestReduce) : null}
                    </Table.Summary.Cell>

                    {/* เงินสด */}
                    <Table.Summary.Cell align="right" style={cellStyle}>
                      {t.netpay ? fmtPos(t.netpay) : null}
                    </Table.Summary.Cell>

                    {/* เงินรับฝากค่างวด */}
                    <Table.Summary.Cell />
                  </Table.Summary.Row>
                );

                return (
                  <>
                    <SummaryRowSummary
                      label="รวมทั้งหมด"
                      count={filteredData.length}
                      t={totalsAll}
                    />
                    <SummaryRowSummary
                      label="รวมยกเลิกทั้งหมด"
                      count={cancelledRows.length}
                      t={totalsCancelled}
                      style={{ background: "#ffebeb" }}
                      cellStyle={{ color: "#dc3545" }}
                    />
                    <SummaryRowSummary
                      label="รวมรับชำระจริง"
                      count={validRows.length}
                      t={totalsValid}
                      style={{ background: "#d4edda" }}
                      cellStyle={{ color: "#155724" }}
                    />
                  </>
                );
              }

              const totalsAll = sumRows(filteredData);
              const totalsCancelled = sumRows(cancelledRows);
              const totalsValid = sumRows(validRows);

              const SummaryRow = ({
                label,
                count,
                t,
                style = {},
                cellStyle = {},
              }) => (
                <Table.Summary.Row
                  style={{
                    background: "#ecf0f1",
                    fontWeight: "bold",
                    ...style,
                  }}
                >
                  {/* 1-5: สาขา → ประเภท */}
                  <Table.Summary.Cell
                    colSpan={5}
                    align="right"
                    style={cellStyle}
                  >
                    {label} {count} รายการ จำนวนเงิน :
                  </Table.Summary.Cell>

                  {/* ค่างวดรวมดอก — role 1/6 */}
                  {showInterestInstallment && (
                    <Table.Summary.Cell align="right" style={cellStyle}>
                      {docType === "normal" && t.payamt
                        ? fmtPos(t.payamt)
                        : null}
                    </Table.Summary.Cell>
                  )}

                  {/* ค่างวดบัญชี — role 1/12 */}
                  {showAccountInstallment && (
                    <Table.Summary.Cell align="right" style={cellStyle}>
                      {docType === "normal" && t.payamtN
                        ? fmtPos(t.payamtN)
                        : null}
                    </Table.Summary.Cell>
                  )}

                  {/* ดาวน์, ปรับ, ขายสด, เงินจอง — empty */}
                  <Table.Summary.Cell />
                  <Table.Summary.Cell />
                  <Table.Summary.Cell />
                  <Table.Summary.Cell />

                  {/* รับอื่นๆ */}
                  <Table.Summary.Cell align="right" style={cellStyle}>
                    {docType === "arothr" && t.payamt ? fmtPos(t.payamt) : null}
                  </Table.Summary.Cell>

                  {/* ส่วนลด */}
                  <Table.Summary.Cell align="right" style={cellStyle}>
                    {t.disct ? fmtPos(t.disct) : null}
                  </Table.Summary.Cell>

                  {/* ดอกเบี้ย */}
                  <Table.Summary.Cell align="right" style={cellStyle}>
                    {t.payamtV ? fmtPos(t.payamtV) : null}
                  </Table.Summary.Cell>

                  {/* เงินสด */}
                  <Table.Summary.Cell align="right" style={cellStyle}>
                    {t.cashPay ? fmtPos(t.cashPay) : null}
                  </Table.Summary.Cell>

                  {/* เงินโอน */}
                  <Table.Summary.Cell align="right" style={cellStyle}>
                    {t.transferPay ? fmtPos(t.transferPay) : null}
                  </Table.Summary.Cell>

                  {/* เงินรับฝากค่างวด */}
                  <Table.Summary.Cell />
                  {/* ✅ รับรวมสุทธิ — เฉพาะ role 1/12 + normal */}
                  {showAccountInstallment && docType === "normal" && (
                    <Table.Summary.Cell align="right" style={cellStyle}>
                      {t.netpay ? fmtPos(t.netpay) : null}
                    </Table.Summary.Cell>
                  )}
                </Table.Summary.Row>
              );

              return (
                <>
                  <SummaryRow
                    label="รวมทั้งหมด"
                    count={filteredData.length}
                    t={totalsAll}
                  />
                  <SummaryRow
                    label="รวมยกเลิกทั้งหมด"
                    count={cancelledRows.length}
                    t={totalsCancelled}
                    style={{ background: "#ffebeb" }}
                    cellStyle={{ color: "#dc3545" }}
                  />
                  <SummaryRow
                    label="รวมรับชำระจริง"
                    count={validRows.length}
                    t={totalsValid}
                    style={{ background: "#d4edda" }}
                    cellStyle={{ color: "#155724" }}
                  />
                </>
              );
            }}
          />
        </Card>
      </Spin>
    </>
  );
};

const MainReportPayment = MotionHoc(Main);
export default MainReportPayment;
